import React, { useState } from "react";

import { CreatePartnerPayoutRequestDto } from "services/partners/restRequests";
import { dateToInputValue, inputValueToDate } from "shared/lib/dateInput";
import { parseMajorAmount } from "shared/lib/price";
import { Button } from "shared/ui/Button";
import { DateField } from "shared/ui/DateField";
import { LabeledField } from "shared/ui/LabeledField";
import { Modal } from "shared/ui/Modal";
import { Select } from "shared/ui/Select";
import { TextInput } from "shared/ui/TextInput";

import { DialogForm } from "../styles";

interface PayoutDialogProps {
    partnerId: number;
    partnerName: string;
    currencies: string[];
    saving: boolean;
    onClose: () => void;
    onSubmit: (payout: CreatePartnerPayoutRequestDto) => void;
}

// Полдень, а не полночь: фильтр периода идёт по локальным дням, и дата не съезжает на соседний.
const inputValueToNoon = (value: string): number | null => {
    const date = inputValueToDate(value);

    return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12).getTime() : null;
};

export const PayoutDialog = ({
    partnerId,
    partnerName,
    currencies,
    saving,
    onClose,
    onSubmit,
}: PayoutDialogProps) => {
    const [amount, setAmount] = useState("");
    const [currencyId, setCurrencyId] = useState(currencies[0]);
    const [paidAt, setPaidAt] = useState(dateToInputValue(new Date()));
    const [comment, setComment] = useState("");

    const parsedAmount = parseMajorAmount(amount);
    const parsedPaidAt = inputValueToNoon(paidAt);
    const canSubmit = !saving && parsedAmount !== null && parsedPaidAt !== null;

    return (
        <Modal
            open
            onClose={onClose}
            title={`Выплата: ${partnerName}`}
            maxWidth="xs"
            actions={
                <>
                    <Button variant="text" color="neutral" onClick={onClose} disabled={saving}>
                        Отмена
                    </Button>
                    <Button
                        onClick={() =>
                            onSubmit({
                                partnerId,
                                amount: parsedAmount,
                                currencyId,
                                paidAt: parsedPaidAt,
                                comment,
                            })
                        }
                        disabled={!canSubmit}
                    >
                        Зафиксировать
                    </Button>
                </>
            }
        >
            <DialogForm>
                <LabeledField
                    label="Сумма"
                    htmlFor="payoutAmount"
                    hint="Только запись в учёте: деньги система не отправляет."
                >
                    <TextInput
                        id="payoutAmount"
                        value={amount}
                        onChange={setAmount}
                        suffix={currencyId}
                        disabled={saving}
                    />
                </LabeledField>

                {currencies.length > 1 && (
                    <LabeledField label="Валюта">
                        <Select
                            value={currencyId}
                            options={currencies.map((id) => ({ value: id, label: id }))}
                            title="Валюта"
                            onChange={setCurrencyId}
                            disabled={saving}
                        />
                    </LabeledField>
                )}

                <LabeledField label="Дата выплаты" htmlFor="payoutPaidAt">
                    <DateField id="payoutPaidAt" value={paidAt} onChange={setPaidAt} disabled={saving} />
                </LabeledField>

                <LabeledField label="Комментарий" htmlFor="payoutComment">
                    <TextInput
                        id="payoutComment"
                        value={comment}
                        onChange={setComment}
                        placeholder="Например, номер платёжки"
                        disabled={saving}
                    />
                </LabeledField>
            </DialogForm>
        </Modal>
    );
};
